(() => {
  const active = new WeakMap();
  const mount = root => {
    if (active.has(root)) return;
    const source = root.querySelector('[data-gp-planter-config]');
    if (!source) return;
    let config;
    try { config = JSON.parse(source.textContent); } catch { return; }
    const definitions = config.product?.options;
    if (!definitions?.length || definitions.length > 2) return;
    const zh = config.language === 'zh';
    const text = value => value?.[config.language] ?? value?.en ?? '';
    const display = (index, value) => definitions[index].values[definitions[index].aliases?.[value] || value];
    const byId = new Map(config.variants.map(v => [String(v.id), v]));
    const findVariant = options => config.variants.find(v => v.options.length === options?.length && v.options.every((value, i) => value === options[i]));
    const resolveVariant = (options, changedIndex) => {
      const exact = findVariant(options);
      if (exact) return exact;
      // Some finishes have their own size range. Keep the explicitly chosen
      // value, then select the nearest available native combination.
      return config.variants.filter(v => v.available && v.options[changedIndex] === options[changedIndex])
        .sort((a, b) => b.options.filter((value, i) => value === options[i]).length - a.options.filter((value, i) => value === options[i]).length)[0];
    };
    const fieldsFor = control => {
      const mainPicker = control.closest('hdt-variant-picker');
      if (mainPicker) return [...mainPicker.querySelectorAll('fieldset')];
      const form = control.closest('hdt-sticky-btn-atc')?.getAttribute('form');
      const picker = [...root.querySelectorAll('hdt-variant-picker')].find(p => p.getAttribute('form') === form);
      return picker && [...picker.querySelectorAll('fieldset')];
    };
    const selected = fields => fields?.map(field => field.querySelector('input[is-value][type="radio"]:checked')?.value);
    const heading = index => text(definitions[index].label) || (definitions[index].role === 'size' ? (zh ? '尺寸' : 'Size') : definitions[index].role === 'colour' ? (zh ? '顏色' : 'Colour') : (zh ? '款式' : 'Style'));
    const priceIndex = Math.max(0, definitions.findIndex(definition => definition.role === 'size'));
    const setText = (element, value) => { if (element && element.textContent !== value) element.textContent = value; };
    const choose = (control, variant) => {
      const fields = fieldsFor(control);
      if (!variant || fields?.length !== definitions.length) return;
      const inputs = fields.map((field, i) => [...field.querySelectorAll('input[is-value][type="radio"]')].find(input => input.value === variant.options[i]));
      if (inputs.some(input => !input || input.disabled)) return;
      const changed = inputs.filter(input => !input.checked);
      if (!changed.length) return;
      // Set the complete combination before one native click. Native theme code
      // updates the price, gallery, availability and both submitted variant IDs.
      for (const input of changed.slice(0, -1)) input.checked = true;
      changed.at(-1).click();
    };
    const enhanceSticky = () => {
      for (const native of root.querySelectorAll('.hdt-sticky-atc__variant-price select[name="id"]')) {
        const fields = fieldsFor(native);
        if (fields?.length !== definitions.length) continue;
        const variant = findVariant(selected(fields)) || byId.get(native.value);
        if (!variant) continue;
        let controls = native.parentElement.querySelector('.gp-planter-sticky-options');
        if (!controls) {
          controls = document.createElement('div'); controls.className = 'gp-planter-sticky-options';
          controls.style.setProperty('--gp-option-count', definitions.length);
          definitions.forEach((definition, index) => {
            const label = document.createElement('label');
            const caption = document.createElement('span'); caption.className = 'gp-planter-sticky-caption'; caption.textContent = heading(index);
            if (index === priceIndex) {
              const price = document.createElement('span'); price.className = 'gp-planter-sticky-price'; price.setAttribute('aria-live', 'polite'); caption.append(price);
            }
            const select = document.createElement('select'); select.className = 'hdt-select hdt-s-text'; select.dataset.gpPlanterOption = index;
            select.setAttribute('aria-label', zh ? `底部${heading(index)}` : `Sticky ${heading(index).toLowerCase()}`);
            for (const value of new Set(config.variants.map(v => v.options[index]))) {
              const labels = display(index, value);
              if (!labels) continue;
              const option = document.createElement('option'); option.value = value; option.textContent = text(labels.sticky); select.append(option);
            }
            label.append(caption, select); controls.append(label);
          });
          native.insertAdjacentElement('afterend', controls);
        }
        setText(controls.querySelector('.gp-planter-sticky-price'), variant.price);
        for (const select of controls.querySelectorAll('select[data-gp-planter-option]')) {
          const index = Number(select.dataset.gpPlanterOption); select.value = variant.options[index];
          for (const option of select.options) {
            const options = [...variant.options]; options[index] = option.value;
            const match = resolveVariant(options, index);
            option.disabled = !match || ![...native.options].some(o => o.value === String(match.id) && !o.disabled);
          }
        }
      }
    };
    const enhance = () => {
      for (const picker of root.querySelectorAll('hdt-variant-picker')) {
        const fields = [...picker.querySelectorAll('fieldset')], values = selected(fields);
        if (fields.length !== definitions.length) continue;
        fields.forEach((field, index) => {
          const current = field.querySelector('[update-value]');
          if (current) {
            const prefix = current.previousSibling, title = zh ? `選擇${heading(index)}： ` : `Choose ${heading(index).toLowerCase()}: `;
            if (prefix?.nodeType === Node.TEXT_NODE && prefix.textContent !== title) prefix.textContent = title;
            setText(current, text(display(index, values[index])?.sticky) || values[index] || '');
          }
          for (const input of field.querySelectorAll('input[is-value][type="radio"]')) {
            const label = input.nextElementSibling, labels = display(index, input.value);
            if (!labels || label?.tagName !== 'LABEL' || label.htmlFor !== input.id) continue;
            if (label.dataset.gpPlanterValue !== input.value) {
              const title = document.createElement('span'); title.className = 'gp-planter-option-size'; title.textContent = text(labels.title);
              label.replaceChildren(...label.querySelectorAll('.sr-only'), title);
              if (text(labels.detail)) { const detail = document.createElement('span'); detail.className = 'gp-planter-option-base'; detail.textContent = text(labels.detail); label.append(detail); }
              if (index === priceIndex) { const price = document.createElement('span'); price.className = 'gp-planter-option-price'; label.append(price); }
              label.setAttribute('aria-label', [text(labels.title), text(labels.detail)].filter(Boolean).join(', '));
              label.dataset.gpPlanterValue = input.value;
            }
            const options = [...values]; options[index] = input.value;
            const variant = findVariant(options);
            setText(label.querySelector('.gp-planter-option-price'), variant?.price || '');
          }
        });
      }
      enhanceSticky();
    };
    const controller = new AbortController();
    root.addEventListener('change', event => {
      const control = event.target;
      if (control.matches?.('select[data-gp-planter-option]')) {
        event.stopPropagation();
        const options = selected(fieldsFor(control));
        if (options?.length === definitions.length) { const index = Number(control.dataset.gpPlanterOption); options[index] = control.value; choose(control, resolveVariant(options, index)); }
        enhanceSticky();
      } else if (control.matches?.('.hdt-sticky-atc__variant-price select[name="id"]')) {
        event.stopPropagation(); choose(control, byId.get(control.value));
      } else if (control.matches?.('hdt-variant-picker input[is-value]')) {
        const fields = fieldsFor(control), options = selected(fields), index = fields?.findIndex(field => field.contains(control));
        if (index >= 0 && !findVariant(options)) {
          const variant = resolveVariant(options, index);
          if (variant) { event.stopImmediatePropagation(); choose(control, variant); }
        }
        queueMicrotask(enhance);
      }
    }, { capture: true, signal: controller.signal });
    const observer = new MutationObserver(enhance);
    active.set(root, { observer, controller });
    enhance(); observer.observe(root, { childList: true, subtree: true });
  };
  const init = (scope = document) => {
    if (scope.matches?.('[data-gp-planter-mobile]')) mount(scope);
    scope.querySelectorAll('[data-gp-planter-mobile]').forEach(mount);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true }); else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    const roots = [...event.target.querySelectorAll('[data-gp-planter-mobile]')];
    if (event.target.matches?.('[data-gp-planter-mobile]')) roots.push(event.target);
    roots.forEach(root => { active.get(root)?.observer.disconnect(); active.get(root)?.controller.abort(); active.delete(root); });
  });
})();
